'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

const Breadcrumb = ({ customItems = null }) => {
  const pathname = usePathname();

  const routeMap = {
    '/product-catalog': { label: 'Products', parent: null },
    '/product-details': { label: 'Product Details', parent: '/product-catalog' },
    '/shopping-cart': { label: 'Cart', parent: null },
    '/checkout': { label: 'Checkout', parent: '/shopping-cart' },
    '/login': { label: 'Login', parent: null }
  };

  const generateBreadcrumbs = () => {
    if (customItems) {
      return customItems;
    }

    const breadcrumbs = [{ label: 'Home', path: '/product-catalog' }];
    const currentRoute = routeMap?.[pathname];

    if (!currentRoute) {
      return breadcrumbs;
    }

    if (currentRoute?.parent) {
      const parentRoute = routeMap?.[currentRoute?.parent];
      if (parentRoute) {
        breadcrumbs?.push({
          label: parentRoute?.label,
          path: currentRoute?.parent
        });
      }
    }

    breadcrumbs?.push({
      label: currentRoute?.label,
      path: pathname,
      isActive: true
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  if (breadcrumbs?.length <= 1) {
    return null;
  }

  return (
    <nav 
      aria-label="Breadcrumb" 
      className="hidden md:block relative py-4 px-4 lg:px-6 bg-background border-b border-border"
    >
      <ol className="flex items-center gap-2 flex-wrap">
        {breadcrumbs?.map((item, index) => {
          const isLast = index === breadcrumbs?.length - 1;
          const isFirst = index === 0;

          return (
            <li key={item?.path || index} className="flex items-center gap-2">
              {!isFirst && (
                <Icon 
                  name="ChevronRightIcon" 
                  size={16} 
                  className="text-muted-foreground hidden sm:block" 
                  aria-hidden="true"
                />
              )}
              {isLast ? (
                <span 
                  className="text-sm font-medium text-foreground caption"
                  aria-current="page"
                >
                  {item?.label}
                </span>
              ) : (
                <Link
                  href={item?.path}
                  className="text-sm font-medium text-muted-foreground hover:text-foreground transition-smooth caption focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded px-1 -mx-1"
                >
                  {item?.label}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
      {/* Mobile abbreviated view */}
      <div className="sm:hidden mt-2">
        <div className="flex items-center gap-2 text-xs caption text-muted-foreground">
          <Icon name="MapPinIcon" size={14} />
          <span>
            {breadcrumbs?.[breadcrumbs?.length - 2]?.label} / {breadcrumbs?.[breadcrumbs?.length - 1]?.label}
          </span>
        </div>
      </div>
    </nav>
  );
};

export default Breadcrumb;